<template>
  <div class="service-pairing-picker">
    <div v-if="value.length" class="selected-list">
      <Tag
        v-for="key in value"
        :key="key"
        type="info"
        closable
        @close="onRemove(key)"
      >
        {{ displayService(key) }}
      </Tag>
    </div>
    <div class="actions">
      <BaseElButton plain :loading="loading" @click="showSelector = true">
        {{ displayButtonText }}
      </BaseElButton>
      <span v-if="maxSelection > 1" class="count">已選 {{ value.length }} / {{ maxSelection }}</span>
    </div>

    <AdvanceTableSelector
      v-if="showSelector"
      :title="selectorTitle"
      :data="options"
      :config="selectorConfig"
      @close="onSelectorClose"
      @confirm="onConfirm"
    />
  </div>
</template>

<script>
import { computed, defineComponent, ref } from 'vue'
import { get, includes, map, filter } from 'lodash'
import Tag from '@/components/Tag/Tag.vue'
import { appointmentServicePairingServiceTypeConfig } from '@/config/appointmentServicePairing'

// 以 pop up 選擇表選服務：服務項目與附加服務在同一張表，可依類型篩選
export default defineComponent({
  name: 'ServicePairingPicker',
  components: {
    Tag,
    AdvanceTableSelector: () => import('@/components/Select/AdvanceTableSelector.vue'),
  },
  props: {
    // 已選的服務 key（toServiceKey）
    value: { type: Array, default: () => [] },
    // 選擇表的資料（useAppointmentServicePairingServiceOptions）
    options: { type: Array, default: () => [] },
    // key → 服務，用來顯示已選服務的名稱
    serviceMap: { type: Object, default: () => ({}) },
    maxSelection: { type: Number, default: 1 },
    // 不能選的服務 key
    disabledKeys: { type: Array, default: () => [] },
    // 已是其他搭配組合主要服務的服務 key：反灰不能選，並標示「已建立組合」
    occupiedKeys: { type: Array, default: () => [] },
    selectorTitle: { type: String, default: '選擇服務' },
    buttonText: { type: String, default: '選擇服務' },
    loading: { type: Boolean, default: false },
  },
  emits: ['input', 'selectorClose'],
  setup (props, { emit }) {
    const showSelector = ref(false)

    // 單選且已選好時改為「更換」（Spec D-4-4）
    const displayButtonText = computed(() => (props.maxSelection === 1 && props.value.length ? '更換' : props.buttonText))

    // 選擇表的篩選欄位會觸發外層表單欄位的驗證，關閉後讓外層重新驗證
    const onSelectorClose = () => {
      showSelector.value = false
      emit('selectorClose')
    }

    const displayService = (key) => {
      const service = get(props.serviceMap, key)
      if (!service) return '-'
      const typeName = get(appointmentServicePairingServiceTypeConfig, [service.type, 'label'], '')
      return `${typeName}｜${service.name}${service.isPublic === false ? '（已下架）' : ''}`
    }

    const onRemove = (key) => {
      emit('input', filter(props.value, (one) => one !== key))
    }

    const onConfirm = (rows) => {
      emit('input', map(rows, 'id'))
    }

    const selectorConfig = computed(() => ({
      selected: [...props.value],
      maxSelection: props.maxSelection,
      disableRow: (row) => includes(props.disabledKeys, row.id) || includes(props.occupiedKeys, row.id),
      pageLimit: 8,
      columns: [
        { label: '類型', key: 'typeName', renderer: (row) => row.typeName },
        {
          label: '服務名稱',
          key: 'name',
          renderer: (row) => [
            row.name,
            row.isPublic === false ? '（已下架）' : '',
            includes(props.occupiedKeys, row.id) ? '（已建立組合）' : '',
          ].join(''),
        },
        { label: '實際價格', key: 'price', renderer: (row) => `$${Number(row.price || 0).toLocaleString()}` },
        { label: '實際預約時間', key: 'bookingTime', renderer: (row) => `${row.bookingTime} 分鐘` },
      ],
      filters: [
        {
          component: () => import('@/components/Select/BaseDataSelect.vue'),
          key: 'type',
          mode: 'equals',
          props: (model) => ({
            placeholder: '全部類型',
            value: model.type,
            filterable: false,
            clearable: true,
            valueKey: 'value',
            labelKey: 'label',
            objKey: 'value',
            dataList: Object.values(appointmentServicePairingServiceTypeConfig),
          }),
          on: (model) => ({
            'update:value': (val) => {
              model.type = val || ''
            },
          }),
        },
        {
          component: () => import('@/components/Input/SearchTextInput.vue'),
          key: 'name',
          mode: 'includes',
          props: (model) => ({
            placeholder: '搜尋服務名稱',
            value: model.name,
          }),
          on: (model) => ({
            'update:value': (val) => {
              model.name = val
            },
          }),
        },
      ],
    }))

    return {
      showSelector,
      displayButtonText,
      onSelectorClose,
      displayService,
      onRemove,
      onConfirm,
      selectorConfig,
    }
  },
})
</script>

<style lang="postcss" scoped>
.service-pairing-picker {
  @apply flex flex-col gap-[8px] w-full;
}
.selected-list {
  @apply flex flex-wrap gap-[8px];
}
.actions {
  @apply flex items-center gap-[12px];
}
.count {
  @apply text-sm text-gray-80;
}
</style>
